/**
 * Назначение: результат чтения CRM в чате помощника — карточки клиента/слотов/записей и таблица для агрегатов.
 * Кто: администратор организации (assistant:read).
 * Вход: ответ чата с read_results.
 */
import { Calendar, Clock, ExternalLink, Search, User, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ROUTE_PATHS } from '../../../app/router/route-paths';
import type { AssistantReadColumn, AssistantReadResult } from '../api/assistant.api';
import { useAssistantChat } from '../model/assistant-chat-context';
import styles from './assistant.module.css';

const TOTAL_LABELS: Record<string, string> = {
  slots: 'Занятий',
  free_slots: 'Свободных занятий',
  windows: 'Окон',
  bookings: 'Записей',
  upcoming: 'Ближайших записей',
  paid_visits: 'Посещений',
  cancellations: 'Отмен',
  occupancy_pct: 'Заполняемость, %',
  revenue_rub: 'Выручка, ₽',
  plan_revenue_rub: 'Тариф организации, ₽',
  refunds_rub: 'Возвраты, ₽',
  payments: 'Платежей',
  avg_check_rub: 'Средний чек, ₽',
  new_clients: 'Новых клиентов',
  active_clients: 'Активных клиентов',
  repeat_clients: 'Повторных клиентов',
  active_subscriptions: 'Активных абонементов',
  expiring_subscriptions: 'Истекает за 30 дней',
  visits_used: 'Визитов использовано',
  subscriptions: 'Абонементов',
  total: 'Всего'
};

function useSafeAssistantChat() {
  try {
    return useAssistantChat();
  } catch {
    return null;
  }
}

function fmtCell(value: unknown, column: AssistantReadColumn): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (column.kind === 'money') {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? `${n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽` : String(value);
  }
  if (column.kind === 'pct') {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? `${n.toLocaleString('ru-RU')} %` : String(value);
  }
  if (column.kind === 'int') {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n.toLocaleString('ru-RU') : String(value);
  }
  return String(value);
}

function fmtTotal(key: string, value: unknown): string {
  if (typeof value === 'number') {
    if (key.endsWith('_rub')) return `${value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
    if (key.endsWith('_pct')) return `${value.toLocaleString('ru-RU')} %`;
    return value.toLocaleString('ru-RU');
  }
  return String(value ?? '—');
}

function Totals({ totals }: { totals?: Record<string, unknown> }) {
  if (!totals || Object.keys(totals).length === 0) return null;
  return (
    <div className={styles.readTotals}>
      {Object.entries(totals).map(([key, value]) => (
        <span key={key} className={styles.readTotal}>
          {TOTAL_LABELS[key] ?? key}: <strong>{fmtTotal(key, value)}</strong>
        </span>
      ))}
    </div>
  );
}

function ProfileCard({ result }: { result: AssistantReadResult }) {
  const chat = useSafeAssistantChat();
  const name = result.title.replace(/^Клиент:\s*/i, '');
  const rows = result.rows.filter((r) => String(r.field ?? '') !== 'Клиент');
  return (
    <div className={`${styles.readCard} ${styles.dataCard}`} data-testid="assistant-read-result">
      <div className={styles.dataCardHeader} data-testid="assistant-read-client-card">
        <span className={styles.dataCardTitle}><User size={15} /> Карточка клиента</span>
        <span className={styles.badgeInfo}>{String(result.totals?.bookings ?? 0)} записей</span>
      </div>
      <div className={styles.profileHead}>
        <strong>{name}</strong>
        {result.notes?.length ? <span className={styles.muted}>{result.notes.join(' · ')}</span> : null}
      </div>
      <div className={styles.profileGrid}>
        {rows.map((row, i) => (
          <div key={i} className={styles.profileRow}>
            <span className={styles.muted}>{String(row.field ?? '')}</span>
            <span>{String(row.value ?? '—')}</span>
          </div>
        ))}
      </div>
      <div className={styles.quickActionsRow} data-testid="assistant-quick-actions">
        <button
          type="button"
          className={styles.quickActionBtn}
          data-testid="assistant-quick-action-freeze"
          onClick={() => chat?.prefillInput(`Заморозь абонемент клиенту ${name}`)}
        >
          ❄️ Заморозить абонемент
        </button>
        <button
          type="button"
          className={styles.quickActionBtn}
          data-testid="assistant-quick-action-book"
          onClick={() => chat?.prefillInput(`Запиши клиента ${name} на занятие`)}
        >
          📅 Записать на занятие
        </button>
        <button
          type="button"
          className={styles.quickActionBtn}
          data-testid="assistant-quick-action-subs"
          onClick={() => chat?.sendText(`Покажи абонементы клиента ${name}`)}
        >
          💳 Абонементы
        </button>
      </div>
      <Totals totals={result.totals} />
    </div>
  );
}

function SlotsCard({ result }: { result: AssistantReadResult }) {
  const chat = useSafeAssistantChat();
  const rows = result.rows.slice(0, 6);
  return (
    <div className={`${styles.readCard} ${styles.dataCard}`} data-testid="assistant-read-result">
      <div className={styles.dataCardHeader} data-testid="assistant-read-slots-card">
        <span className={styles.dataCardTitle}><Calendar size={15} /> Занятия в расписании</span>
        <span className={styles.badgeInfo}>{result.rows.length} слотов</span>
      </div>
      <div className={styles.dataCardList}>
        {rows.map((row, i) => {
          const free = Number(row.free ?? 0);
          const capacity = Number(row.capacity ?? 0);
          const date = String(row.date ?? '');
          const slotId = row.slot_id;
          return (
            <div key={`${row.slot_id ?? i}`} className={styles.dataCardItem}>
              <div className={styles.dataCardItemHeader}>
                <div className={styles.dataCardTitle}>
                  <Clock size={14} className={styles.muted} />
                  <span>{String(row.time ?? '')}</span>
                  <span>·</span>
                  <span>{String(row.service ?? '')}</span>
                </div>
                <span className={free > 0 ? styles.badgeSuccess : styles.badgeNeutral}>
                  {free > 0 ? `Свободно: ${free} из ${capacity}` : 'Мест нет'}
                </span>
              </div>
              <div className={styles.dataCardItemDetails}>
                {row.trainer ? <span>Тренер: {String(row.trainer)}</span> : null}
                {row.space ? <span>Зал: {String(row.space)}</span> : null}
                {row.branch ? <span>Филиал: {String(row.branch)}</span> : null}
                <span>Дата: {date}</span>
              </div>
              <div className={styles.dataCardActions}>
                {slotId ? (
                  <>
                    <button
                      type="button"
                      className={styles.quickActionBtn}
                      data-testid="assistant-quick-action-slot-book"
                      onClick={() => chat?.prefillInput(`Запиши клиента на занятие #${slotId}`)}
                    >
                      Записать сюда
                    </button>
                    <button
                      type="button"
                      className={styles.quickActionBtn}
                      data-testid="assistant-quick-action-slot-whos-booked"
                      onClick={() => chat?.sendText(`Кто записан на занятие #${slotId}?`)}
                    >
                      Кто записан?
                    </button>
                  </>
                ) : null}
                <Link to={`${ROUTE_PATHS.schedule}?date=${encodeURIComponent(date)}`} className={styles.dataCardLink}>
                  В расписание <ExternalLink size={12} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
      <Totals totals={result.totals} />
    </div>
  );
}

function BookingsCard({ result }: { result: AssistantReadResult }) {
  const chat = useSafeAssistantChat();
  return (
    <div className={`${styles.readCard} ${styles.dataCard}`} data-testid="assistant-read-result">
      <div className={styles.dataCardHeader} data-testid="assistant-read-bookings-card">
        <span className={styles.dataCardTitle}><Users size={15} /> Записанные клиенты</span>
        <span className={styles.badgeInfo}>{result.rows.length} чел.</span>
      </div>
      {result.rows.length === 0 ? (
        <p className={styles.muted}>На это занятие ещё никто не записан.</p>
      ) : (
        <div className={styles.dataCardList}>
          {result.rows.slice(0, 8).map((row, i) => (
            <div key={`${row.client ?? i}-${i}`} className={styles.dataCardItem}>
              <div className={styles.dataCardItemHeader}>
                <div className={styles.dataCardTitle}>
                  <User size={13} className={styles.muted} />
                  <span>{String(row.client ?? '')}</span>
                  {row.minor ? <span className={styles.muted}>(ребёнок: {String(row.minor)})</span> : null}
                </div>
                <span className={row.paid ? styles.badgeSuccess : styles.badgeNeutral}>
                  {String(row.status ?? '')}
                </span>
              </div>
              <div className={styles.dataCardItemDetails}>
                {row.phone ? <span>Тел: {String(row.phone)}</span> : null}
                {row.paid ? <span>Оплачено</span> : <span>Без оплаты</span>}
              </div>
              {row.client ? (
                <div className={styles.dataCardActions}>
                  <button
                    type="button"
                    className={styles.quickActionBtn}
                    data-testid="assistant-quick-action-client-profile"
                    onClick={() => chat?.sendText(`Покажи профиль клиента ${String(row.client)}`)}
                  >
                    Профиль
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      <Totals totals={result.totals} />
    </div>
  );
}

function ClientsCard({ result }: { result: AssistantReadResult }) {
  const chat = useSafeAssistantChat();
  return (
    <div className={`${styles.readCard} ${styles.dataCard}`} data-testid="assistant-read-result">
      <div className={styles.dataCardHeader} data-testid="assistant-read-clients-card">
        <span className={styles.dataCardTitle}><Search size={15} /> Найдено клиентов</span>
        <span className={styles.badgeInfo}>{result.rows.length}</span>
      </div>
      <div className={styles.dataCardList}>
        {result.rows.map((row, i) => {
          const clientName = String(row.name ?? '');
          return (
            <div key={`${row.client_id ?? i}`} className={styles.dataCardItem}>
              <div className={styles.dataCardItemHeader}>
                <span>{clientName}</span>
                <span className={styles.badgeInfo}>ID {String(row.client_id ?? '')}</span>
              </div>
              <div className={styles.dataCardItemDetails}>
                {row.phone ? <span>Тел: {String(row.phone)}</span> : null}
                {row.created_at ? <span>Создан: {String(row.created_at)}</span> : null}
              </div>
              <div className={styles.dataCardActions}>
                <button
                  type="button"
                  className={styles.quickActionBtn}
                  data-testid="assistant-quick-action-client-profile"
                  onClick={() => chat?.sendText(`Покажи профиль клиента ${clientName}`)}
                >
                  Профиль
                </button>
                <button
                  type="button"
                  className={styles.quickActionBtn}
                  data-testid="assistant-quick-action-client-book"
                  onClick={() => chat?.prefillInput(`Запиши клиента ${clientName} на занятие`)}
                >
                  Записать
                </button>
                <button
                  type="button"
                  className={styles.quickActionBtn}
                  data-testid="assistant-quick-action-client-freeze"
                  onClick={() => chat?.prefillInput(`Заморозь абонемент клиенту ${clientName}`)}
                >
                  Заморозить
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <Totals totals={result.totals} />
    </div>
  );
}

function TableCard({ result }: { result: AssistantReadResult }) {
  return (
    <div className={styles.readCard} data-testid="assistant-read-result">
      <div className={styles.proposalHeader}>
        <div>
          <p className={styles.proposalTitle}>{result.title}</p>
          {result.period ? (
            <p className={styles.proposalMeta}>
              Период: {result.period.from}–{result.period.to}
            </p>
          ) : null}
        </div>
      </div>
      <Totals totals={result.totals} />
      <div className={styles.readTableWrap}>
        <table className={styles.readTable}>
          <thead>
            <tr>
              {result.columns.map((c) => (
                <th key={c.key}>{c.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(result.columns.length, 1)} className={styles.readEmpty}>
                  Данных нет
                </td>
              </tr>
            ) : (
              result.rows.map((row, i) => (
                <tr key={i}>
                  {result.columns.map((c) => (
                    <td key={c.key}>{fmtCell(row[c.key], c)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {result.notes && result.notes.length > 0 ? (
        <ul className={styles.readNotes}>
          {result.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function AssistantReadResultCard({ result }: { result: AssistantReadResult }) {
  switch (result.tool) {
    case 'clients.profile':
      return <ProfileCard result={result} />;
    case 'schedule.slots.list':
      return <SlotsCard result={result} />;
    case 'bookings.for_slot.list':
      return <BookingsCard result={result} />;
    case 'clients.search':
      return <ClientsCard result={result} />;
    default:
      return <TableCard result={result} />;
  }
}
